import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Trophy, Flame } from 'lucide-react';
import { api } from '@/lib/api';
import { EntryCard, Leaderboard } from '@/components/feed';
import { LoadingState, EmptyState, ErrorState } from '@/components/states';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

export default function ChallengeDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [board, setBoard] = useState([]);
  const [sort, setSort] = useState('trending');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [c, lb] = await Promise.all([
        api.get(`/challenges/${slug}`, { params: { sort } }),
        api.get(`/challenges/${slug}/leaderboard`),
      ]);
      setData(c.data);
      setBoard(lb.data.rankings);
    } catch (e) {
      setError('Challenge introuvable');
    } finally {
      setLoading(false);
    }
  }, [slug, sort]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const queue = data.entries.map((e) => ({
    song_id: e.song_id, title: e.title, slug: e.slug, cover_url: e.cover_url,
    audio_url: e.audio_url, style_text: e.style_text, duration_seconds: e.duration_seconds,
    creator: e.creator?.display_name,
  }));

  return (
    <div className="space-y-8">
      <div className="card-surface relative overflow-hidden p-8">
        <img src={data.cover_url} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
        <div className="relative">
          <span className="inline-flex items-center gap-2 rounded-full bg-brand/20 px-3 py-1 font-mono text-xs uppercase tracking-widest text-brand">
            {data.status === 'active' ? <><Flame className="h-3.5 w-3.5" /> En cours</> : data.status}
          </span>
          <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            {data.emoji} {data.title}
          </h1>
          <p className="mt-2 max-w-xl text-muted-foreground">{data.description}</p>
          <p className="mt-2 font-mono text-sm text-brand">{data.entries_count} participations</p>
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="mb-4 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="font-display text-xl font-semibold text-white">Participations</h2>
            <Tabs value={sort} onValueChange={setSort} className="w-full sm:w-auto">
              <TabsList className="w-full justify-start overflow-x-auto bg-surface-card sm:w-auto">
                <TabsTrigger value="trending" data-testid="sort-trending">Tendances</TabsTrigger>
                <TabsTrigger value="top" data-testid="sort-top">Plus aimées</TabsTrigger>
                <TabsTrigger value="recent" data-testid="sort-recent">Récentes</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          {data.entries.length === 0 ? (
            <EmptyState icon={Trophy} title="Aucune participation" description="Sois le premier à publier ta musique dans ce challenge depuis ta Bibliothèque." />
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {data.entries.map((e) => (
                <EntryCard key={e.entry_id} entry={e} queue={queue} onLikeChange={() => load()} />
              ))}
            </div>
          )}
        </div>
        <div>
          <h2 className="mb-4 flex items-center gap-2 font-display text-xl font-semibold text-white">
            <Trophy className="h-5 w-5 text-brand" /> Classement
          </h2>
          {board.length === 0 ? (
            <p className="text-sm text-muted-foreground">Le classement apparaîtra dès les premiers votes.</p>
          ) : <Leaderboard rankings={board} />}
        </div>
      </div>
    </div>
  );
}
